<script>
    import { onMount, tick } from "svelte";

    import Select from "svelte-select/Select.svelte";
    import { get } from "svelte/store";

    import {
        CreateBillingRuleModal,
        CustomTooltip,
        Loader,
        NotificationModal,
        PageLoading,
    } from "./index.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import { isObjectEmpty, showError } from "../../Utils/utilFunctions.svelte";

    import {
        asaAllItemsList,
        mbsAllItemsList,
        user
    } from "../../stores";

    import {
        cacheAsaItems,
        cacheMbsItems,
    } from "../../Utils/utilCache.svelte";

    import { searchMbsItems as searchMbs } from "../../Pages/PatientEncounter/helpers/mbsHelper.svelte";
    import { searchAsaItems as searchAsa } from "../../Pages/PatientEncounter/helpers/asaHelper.svelte";

    import {
        getOptionLabel,
        getSubmitBtnDisable,
        getTotalUnits,
        syncItemsByItemType,
        syncBillingRules,
        getInsuranceTypes,
        getSelectedItems,
    } from "../../Pages/Presets/helpers/presetsHelper.svelte";

    import { DirectUserRoles, presetRuleType } from "../../Utils/utilEnums.svelte";
    import InfoIcon from "./InfoIcon.svelte";

    export let patientEncounter = {};
    export let closeModal = () => {};
    export let onSubmit = () => {};
    export let selectedPreset = {};
    export let doctorDetails = {};
    export let reloadBillingRules = () => {};

    $: isSavePresetFromEncounter = !isObjectEmpty(patientEncounter);

    $: isSubmitBtnDisabled = getSubmitBtnDisable(
        presetData,
        allInsuranceTypes,
        totalUnits,
    ).disable;

    $: submitBtnTooltip = getSubmitBtnDisable(
        presetData,
        allInsuranceTypes,
        totalUnits,
    ).tooltip;

    $: totalUnits = getTotalUnits(
        presetData?.useAsaCodes,
        selectedAsaItems,
        selectedMbsItems,
        presetData?.procedureLength
    );

    $: showCreateBillingRuleIcon = true;

    let latestInvoiceData = {},
        presetData = { useAsaCodes: false },
        isCreatePreset = true,
        isDuplicatePreset = false,
        isCreatingPreset = false;

    let hoveredIndexes = [];

    let selectedMbsItems = [],
        selectedAsaItems = [];

    let searchMbsItems = [],
        searchAsaItems = [];

    let isLoadingDoctorDetails = false,
        doctorProviders = [],
        doctorBillingRules = [],
        allInsuranceTypes = [];

    let isSyncingItemUnits = false,
        isSyncingInsuranceTypes = false;

    let showNonPreferredADF = true;

    let isCreateNewBillingRuleModalOpen = false,
        newBillingRuleObj = {},
        selectedInsuranceTypeIndex,
        isCreatingNewBillingRule = false;

    let isLoadingPreOpQuestionnaire = false,
        filteredPreOpQuestionnaires = [],
        selectedPreOpQuestionnaire;

    onMount(async () => {
        isSavePresetFromEncounter && (await initPresetFromEncounter());
        !isSavePresetFromEncounter && (await initPresetInGeneral());

        presetData.accountId = isSavePresetFromEncounter
            ? patientEncounter.doctorId
            : doctorDetails.id;

        showNonPreferredADF = doctorProviders.some(
            ({ isPreferredDefence }) => !isPreferredDefence,
        );

        initInsuranceTypes();

        loadPreOpQuestionnaires();

        // sync encounter provider to related insurance type
        if (isSavePresetFromEncounter) await mapInsuranceType();

       /*  // pre-select default provider if no provider selected
        allInsuranceTypes = initDefaultSelectedProvider(
            allInsuranceTypes,
            doctorProviders,
        ); */

        // init NIB insurance type text
        allInsuranceTypes.map((it) => {
            if (it.hasOwnProperty("doctorProviderId"))
                handleSelectProvider(it.presetRuleType, it.doctorProviderId);
        });
    });

    const initPresetFromEncounter = async () => {
        const { billingData, procedureName, doctorId } = patientEncounter;

        latestInvoiceData = billingData.find((bd) => bd.isLatestInvoiceData);

        if (!latestInvoiceData) {
            showError("No latest invoice data under this encounter");
            closeModal();
            return;
        }

        const { useAsaCodes, asaData, mbsData } = latestInvoiceData;

        presetData.accountId = doctorId;
        presetData.name = procedureName;
        presetData.useAsaCodes = useAsaCodes;
        presetData.isFromEncounter = true;

        if (useAsaCodes) selectedAsaItems = [...asaData];
        else selectedMbsItems = [...mbsData];

        await loadDoctorDetails(doctorId);
    };

    const initPresetInGeneral = async () => {
        isDuplicatePreset = selectedPreset.hasOwnProperty("fromId") && (!selectedPreset.hasOwnProperty("id") || !selectedPreset.id );
        isCreatePreset = !selectedPreset.hasOwnProperty("id") || !selectedPreset.id ;

        presetData = { ...selectedPreset };

        if (isCreatePreset && !isDuplicatePreset) {
            presetData.useAsaCodes = false;
        } else {
            await apiMethods.getMethod
                .getAccountPresetById(isDuplicatePreset ? presetData.fromId : presetData.id)
                .then((res) => (presetData = { ...res }));

            selectedMbsItems = presetData.mbsCodes;
            selectedAsaItems = presetData.asaCodes;

            await syncItems();
        }

        const { providers, billingRules } = doctorDetails;

        doctorProviders = [...providers];
        doctorBillingRules = [...billingRules];
    };

    const initInsuranceTypes = () => {
        const defaultBillingRule = {
            accountId: isSavePresetFromEncounter
                ? patientEncounter.doctorId
                : doctorDetails.id,
            billingRuleId: 0,
            doctorProviderId: 0,
        };

        allInsuranceTypes = getInsuranceTypes(
            presetData,
            allInsuranceTypes,
            defaultBillingRule,
            showNonPreferredADF,
        );
    };

    const loadPreOpQuestionnaires = async () => {
        isLoadingPreOpQuestionnaire = true;

         //load preopquestionnaires by selected doctor
        const searchParams = {};
        const userDetails = get(user);

        if ((userDetails.role == DirectUserRoles.Manager || userDetails.role == DirectUserRoles.Admin) && userDetails.groupId)
            searchParams["filter[groupId]"] = `0,${userDetails.groupId}`; //if it's manager, load all questionnaires from group
        else 
            searchParams["filter[doctorId]"] = doctorDetails.id; //if it's doctor, load only doctor's questionnaires
       
        await apiMethods.getMethod
            .getPreOpQuestionnaires(0, 999999, searchParams)
            .then((res) => {
                if (!res || res.error) {
                    showError(
                        res.error ??
                            "Failed to load Pre-Op Questionnaire",
                    );
                } else filteredPreOpQuestionnaires = [...res.results];
            })

        if (
            presetData.preOpQuestionnaireId &&
            !!filteredPreOpQuestionnaires.length &&
            filteredPreOpQuestionnaires.some(
                (q) => q.id === presetData.preOpQuestionnaireId,
            )
        )
            selectedPreOpQuestionnaire = filteredPreOpQuestionnaires.find(
                (q) => q.id === presetData.preOpQuestionnaireId,
            );

        isLoadingPreOpQuestionnaire = false;
    };

    const initDefaultSelectedProvider = (insurances, providers) => {
        const defaultProvider = providers.find((p) => p.isDefault);

        if (!defaultProvider) return insurances;

        return insurances.map((insurance) => {
            // if insurance has selected provider OR in edit flow the provider was selected as NONE, return insurance as is
            if (
                insurance.doctorProviderId ||
                (selectedPreset.hasOwnProperty("id") &&
                    !insurance.doctorProviderId)
            )
                return insurance;

            // if default provider is preferredDefence, insurance Non-preferred ADF will not show this provider in dropdown options, thus use 0 as default selected option
            return {
                ...insurance,
                doctorProviderId: defaultProvider.isPreferredDefence
                    ? insurance.presetRuleType == 6
                        ? 0
                        : defaultProvider.id
                    : defaultProvider.id,
            };
        });
    };

    const mapInsuranceType = async () => {
        const { doctorProvider } = patientEncounter;
        const { patientInsuranceType, patientInsuranceId } = latestInvoiceData;

        let targetPresetInsuranceTypeIndex;

        switch (patientInsuranceType) {
            case 0:
                const fund = await apiMethods.getMethod
                    .getInsuranceFundUnitValue(patientInsuranceId)
                    .then((res) => res);

                if (fund) {
                    const { insuranceCode } = fund;

                    targetPresetInsuranceTypeIndex =
                        insuranceCode == "NIB"
                            ? 2
                            : ["CDH", "LHS", "AUH"].includes(
                                    insuranceCode.toUpperCase(),
                                )
                              ? 1
                              : 0;
                }
                break;
            case 10:
                targetPresetInsuranceTypeIndex = 4;
                break;
            case 11:
                targetPresetInsuranceTypeIndex = 3;
                break;
            case 15:
            case 18:
                targetPresetInsuranceTypeIndex = 5;
                break;
            case 26:
                targetPresetInsuranceTypeIndex = 6;
                break;
            default:
                break;
        }

        if (
            targetPresetInsuranceTypeIndex ||
            targetPresetInsuranceTypeIndex != undefined
        )
            allInsuranceTypes[targetPresetInsuranceTypeIndex].doctorProviderId =
                doctorProvider.id;
    };

    const loadDoctorDetails = async (doctorId) => {
        isLoadingDoctorDetails = true;

        await apiMethods.getMethod
            .getAccountById(doctorId)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load doctor details, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    doctorProviders = [...res.providers];
                    doctorBillingRules = [...res.billingRules];
                }
            })
            .catch((err) => showError(err));

        isLoadingDoctorDetails = false;
    };

    const syncItems = async () => {
        isSyncingItemUnits = true;

        if (presetData.useAsaCodes) {
            await cacheAsaItems();

            selectedAsaItems = selectedAsaItems.map((item) => {
                const { itemCode, subItemCode } = item;

                const targetItem = $asaAllItemsList.find(
                    (itm) =>
                        itm.itemCode == itemCode &&
                        itm.subItemCode == subItemCode,
                );

                if (targetItem)
                    return {
                        ...targetItem,
                        amaUnits: targetItem.amaUnits ?? 0,
                    };

                return { ...item, amaUnits: 0 };
            });
        } else {
            await cacheMbsItems();

            selectedMbsItems = selectedMbsItems.map((item) => {
                const { itemNum, subItemNum } = item;

                const targetItem = $mbsAllItemsList.find(
                    (itm) =>
                        itm.itemNum == itemNum && itm.subItemNum == subItemNum,
                );

                if (targetItem)
                    return {
                        ...targetItem,
                        basicUnits: targetItem.basicUnits ?? 0,
                    };

                return { ...item, basicUnits: 0 };
            });
        }

        isSyncingItemUnits = false;
    };

    const handleModifyPreset = async () => {
        isCreatingPreset = true;

        await syncPresetData();

        isCreatePreset
            ? await createAccountPreset()
            : await updateAccountPreset();

        closeModal(true);

        isCreatingPreset = false;

        onSubmit();
    };

    const createAccountPreset = async () => {
        await apiMethods.postMethod
            .createAccountPresets(presetData)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ||
                            "Failed to create preset, please refresh the page and try again and contact support if the issue persists.",
                    );
            })
            .catch((err) => showError(err));
    };

    const updateAccountPreset = async () => {
        await apiMethods.putMethod
            .updateAccountPresets(presetData)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to update preset, please refresh the page and try again and contact support if the issue persists.",
                    );
            })
            .catch((err) => showError(err));
    };

    const syncPresetData = async () => {
        presetData = syncItemsByItemType(
            presetData,
            selectedMbsItems,
            selectedAsaItems,
        );

        presetData = syncBillingRules(presetData, allInsuranceTypes);

        if (
            selectedPreOpQuestionnaire &&
            !isObjectEmpty(selectedPreOpQuestionnaire) &&
            selectedPreOpQuestionnaire.id
        )
            presetData.preOpQuestionnaireId = selectedPreOpQuestionnaire.id;
    };

    const handleSelectItemType = (type = "") => {
        hoveredIndexes = [];

        presetData.useAsaCodes = type == "asa";
        if(type == "mbs") {
            presetData.procedureLength = 0; //reset for MBS items
        }

        initInsuranceTypes();

        if (presetData.useAsaCodes)
            allInsuranceTypes = allInsuranceTypes.map((it) => {
                const selectedBillingRule = doctorBillingRules.find(
                    (dbr) => dbr.id == it.billingRuleId,
                );

                if (!selectedBillingRule) return it;

                if (selectedBillingRule.type != 2) it.billingRuleId = 0;

                return it;
            });

        // init NIB insurance type text
        allInsuranceTypes.map((it) => {
            if (it.hasOwnProperty("doctorProviderId"))
                handleSelectProvider(it.presetRuleType, it.doctorProviderId);
        });
    };

    const loadItems = async (searchText = "", type) => {
        if (!searchText) return [];

        const isSearchingMbsItems = type == "mbs";

        let res = isSearchingMbsItems
            ? await searchMbs(searchText)
            : await searchAsa(searchText);

        if (!res || !res.length) return [];

        const result = [];

        res.filter((item) =>
            isSearchingMbsItems
                ? !selectedMbsItems.some(
                      (itm) =>
                          item.itemNum == itm.itemNum &&
                          item.subItemNum == itm.subItemNum,
                  )
                : !selectedAsaItems.some(
                      (itm) =>
                          item.itemCode == itm.itemCode &&
                          item.subItemCode == itm.subItemCode,
                  ),
        ).map((item) =>
            result.push({
                ...item,
                displayName: getOptionLabel(item, type),
            }),
        );

        return result;
    };

    const handleSelectItem = (item, type = "") => {
        if (type == "mbs")
            selectedMbsItems = getSelectedItems(selectedMbsItems, item);
        else selectedAsaItems = getSelectedItems(selectedAsaItems, item);

        clearSelectItem(type);
    };

    const clearSelectItem = (type = "") => {
        if (type == "mbs") searchMbsItems.item = null;
        else searchAsaItems.item = null;
    };

    const handleRemoveSelectedItem = (index) => {
        if (presetData.useAsaCodes) {
            if(index > selectedAsaItems.length) {
                //clicked x on time
                presetData.procedureLength = 0;
            }
            selectedAsaItems = selectedAsaItems.toSpliced(index, 1);

        }
        else selectedMbsItems = selectedMbsItems.toSpliced(index, 1);
    };

    const handleSelectProvider = (typeIndex, providerId) => {
        /* const isNIBInsurance =
            presetRuleType.find((p) => p.id == typeIndex)?.type == "NIBGroupFunds";

        if (!isNIBInsurance) return;

        if (isNIBInsurance) {
            const selectedProvider = doctorProviders.find(
                (dp) => dp.id == Number(providerId),
            );

            if (selectedProvider) {
                // hide NIB gap fee checkbox if selected provider is MediGap
                var nibInsuranceType = allInsuranceTypes.find((i) => i.presetRuleType == typeIndex);
                nibInsuranceType.showCapCheckbox =
                    !selectedProvider.isNoGapNib;
                nibInsuranceType.NibGapLabel = nibInsuranceType.showCapCheckbox
                    ? "Billing selector for procedures with 5 units or more"
                    : ""; // "Any gap charged will result in a lower patient rebate"
                allInsuranceTypes = [...allInsuranceTypes]; //refresh the obj
            }
        } */
    };

    const handleClickCreateBillingRuleIcon = (insuranceTypeIndex) => {
        _openCreateNewBillingRuleModal();

        if (!isNaN(insuranceTypeIndex))
            selectedInsuranceTypeIndex = insuranceTypeIndex;
    };

    const handleAfterCreateBillingRule = async (newBillingRule = {}) => {
        isCreatingNewBillingRule = true;

        await loadDoctorDetails(
            isSavePresetFromEncounter
                ? patientEncounter.doctorId
                : doctorDetails.id,
        );

        // sync providers & billing rules in doctorDetails
        if (!isSavePresetFromEncounter) {
            doctorDetails = {
                ...doctorDetails,
                providers: [...doctorProviders],
                billingRules: [...doctorBillingRules],
            };
            reloadBillingRules(doctorDetails);
        }

        // Assign selected insurance type's billing type to the new one if it has no billing type selected
        if (
            newBillingRule &&
            !isObjectEmpty(newBillingRule) &&
            newBillingRule.id &&
            !isNaN(selectedInsuranceTypeIndex)
        )
            allInsuranceTypes = allInsuranceTypes
                .filter((insurance) => !insurance.isHidden)
                .map((insuranceType, i) => {
                    if (i == selectedInsuranceTypeIndex)
                        insuranceType.billingRuleId = newBillingRule.id;

                    return insuranceType;
                });

        isCreatingNewBillingRule = false;
    };

    // HELPER FUNCTIONS
    const _openCreateNewBillingRuleModal = () => {
        isCreateNewBillingRuleModalOpen = true;
        newBillingRuleObj = {};
        selectedInsuranceTypeIndex = null;
    };

    const _closeCreateNewBillingRuleModal = () => {
        isCreateNewBillingRuleModalOpen = false;
        newBillingRuleObj = {};
    };

    $: visibleInsuranceTypes =
        allInsuranceTypes.filter(i => !i.isHidden);

    $: insuranceTypesByGroup = visibleInsuranceTypes.reduce((acc, item) => {
        if (item.presetGroup) {
            acc[item.presetGroup] ??= [];
            acc[item.presetGroup].push(item);
        } else {
            acc.__ungrouped ??= [];
            acc.__ungrouped.push(item);
        }
        return acc;
    }, {});
</script>

<NotificationModal
    isModalOpen
    title={`${isSavePresetFromEncounter ? "Save encounter as preset" : `${isDuplicatePreset ? "Duplicate" : isCreatePreset ? "Create" : "Edit"} preset`}`}
    closeModal={() => closeModal()}
    closeModalFunction={() => closeModal()}
    isConfirming={isCreatingPreset}
    modalMaxWidth="max-w-6xl"
    showConfirmBtn={false}
    noContentPadding
    btnOptions={[
        {
            text: isCreatingPreset ? "Submitting" : "Submit",
            loading: isCreatingPreset || isSubmitBtnDisabled,
            function: () => handleModifyPreset(),
            class: isSubmitBtnDisabled ? "tooltip tooltip-secondary" : "",
            tooltip: submitBtnTooltip,
        },
    ]}
>
    <div class="flex flex-1 gap-x-4 max-h-[600px] p-6 overflow-y-auto">
        <div class="flex flex-col flex-1 gap-y-4">
            <div class="flex gap-x-4">
                <p class="title mandatory">Name:</p>
                <input
                    type="text"
                    class="input input-sm input-bordered flex-1 w-remaining"
                    autocomplete="off"
                    bind:value={presetData.name}
                />
            </div>

            <div class="flex gap-x-4">
                <p class="title">Item Type:</p>

                <div class="flex flex-1 gap-x-4  w-remaining">
                    <button
                        class={`btn btn-sm rounded rounded-full text-xs ${presetData.useAsaCodes ? "btn-secondary" : "btn-primary"}`}
                        on:click|preventDefault={() =>
                            handleSelectItemType("mbs")}
                    >
                        MBS
                    </button>

                    <button
                        class={`btn btn-sm rounded rounded-full text-xs ${presetData.useAsaCodes ? "btn-primary" : "btn-secondary"}`}
                        on:click|preventDefault={() =>
                            handleSelectItemType("asa")}
                    >
                        ASA
                    </button>
                </div>
            </div>

            <div class="flex items-center gap-x-4">
                <p class="title">Preset items:</p>

                {#if presetData.useAsaCodes}
                    <Select
                        bind:value={searchAsaItems.item}
                        class="flex flex-1 max-h-[35px] rounded-full  w-remaining"
                        loadOptions={(e) => loadItems(e, "asa")}
                        itemId="id"
                        label="displayName"
                        on:select={() =>
                            handleSelectItem(searchAsaItems.item, "asa")}
                        on:clear={() => clearSelectItem("asa")}
                        noOptionsMessage="No ASA item found"
                        placeholder="Search ASA items"
                    />
                {:else}
                    <Select
                        bind:value={searchMbsItems.item}
                        class="flex flex-1 max-h-[35px] rounded-full  w-remaining"
                        loadOptions={(e) => loadItems(e, "mbs")}
                        itemId="id"
                        label="displayName"
                        on:select={() =>
                            handleSelectItem(searchMbsItems.item, "mbs")}
                        on:clear={() => clearSelectItem("mbs")}
                        noOptionsMessage="No MBS item found"
                        placeholder="Search MBS items"
                    />
                {/if}
            </div>
            {#if presetData.useAsaCodes}
                <div class="flex gap-x-4">
                    <p class="title">Time (minutes):</p>
                    <input
                        type="number"
                        class="input input-sm input-bordered flex-1  w-remaining"
                        autocomplete="off"
                        bind:value={presetData.procedureLength}
                    />
                </div>
            {/if}

            <div class="flex items-center gap-x-4">
                <p class="title">Item numbers:</p>

                <p>
                    {!presetData
                        ? "Sync data..."
                        : isSyncingItemUnits
                          ? "Calculating total units..."
                          : `${totalUnits} total units`}
                </p>
            </div>

            <div
                class="flex flex-1 flex-col py-2 gap-y-2 overflow-y-auto bg-gray-200/50 rounded-lg"
            >
                {#if isSyncingItemUnits}
                    <div
                        class="flex w-full justify-center items-center gap-x-2"
                    >
                        <Loader />
                        <p class="cursor-default">Loading items...</p>
                    </div>
                {:else if presetData.useAsaCodes}
                    {#each selectedAsaItems as { itemCode, subItemCode, infoShortTitle }, i}
                        <div
                            class="preset-item flex items-start justify-between px-4 {hoveredIndexes[
                                i
                            ]
                                ? 'hovered'
                                : ''}"
                        >
                            <div class="flex w-full gap-x-2">
                                <p class="cursor-default w-1/4">
                                    {itemCode}
                                    {subItemCode !== "" &&
                                    subItemCode !== "0" &&
                                    subItemCode !== 0
                                        ? `- ${subItemCode}`
                                        : ""}
                                </p>
                                <p class="text-left cursor-default w-3/4">
                                    {infoShortTitle}
                                </p>
                            </div>
                            <p
                                class="remove-button text-error cursor-pointer"
                                on:click|preventDefault={() =>
                                    handleRemoveSelectedItem(i)}
                                on:mouseenter={() => (hoveredIndexes[i] = true)}
                                on:mouseleave={() =>
                                    (hoveredIndexes[i] = false)}
                            >
                                X
                            </p>
                        </div>
                    {/each}
                    {#if presetData.procedureLength}
                        <div
                            class="preset-item flex items-start justify-between px-4 {hoveredIndexes[
                                selectedAsaItems.length + 1
                            ]
                                ? 'hovered'
                                : ''}"
                        >
                            <div class="flex w-full gap-x-2">
                                <p class="cursor-default w-1/4">
                                    Time
                                </p>
                                <p class="text-left cursor-default w-3/4">
                                    {presetData.procedureLength} minutes
                                </p>
                            </div>
                            <p
                                class="remove-button text-error cursor-pointer"
                                on:click|preventDefault={() =>
                                    handleRemoveSelectedItem(selectedAsaItems.length + 1)}
                                on:mouseenter={() => (hoveredIndexes[selectedAsaItems.length + 1] = true)}
                                on:mouseleave={() =>
                                    (hoveredIndexes[selectedAsaItems.length + 1] = false)}
                            >
                                X
                            </p>
                        </div>
                    {/if}
                {:else}
                    {#each selectedMbsItems as { itemNum, subItemNum, infoShortTitle }, i}
                        <div
                            class="preset-item flex items-start justify-between px-4 {hoveredIndexes[
                                i
                            ]
                                ? 'hovered'
                                : ''}"
                        >
                            <div class="flex w-full gap-x-2">
                                <p class="cursor-default w-1/4">
                                    {itemNum}
                                    {subItemNum !== "" &&
                                    subItemNum !== "0" &&
                                    subItemNum !== 0
                                        ? `- ${subItemNum}`
                                        : ""}
                                </p>
                                <p class="text-left cursor-default w-3/4">
                                    {infoShortTitle}
                                </p>
                            </div>
                            <p
                                class="remove-button text-error cursor-pointer"
                                on:click|preventDefault={() =>
                                    handleRemoveSelectedItem(i)}
                                on:mouseenter={() => (hoveredIndexes[i] = true)}
                                on:mouseleave={() =>
                                    (hoveredIndexes[i] = false)}
                            >
                                X
                            </p>
                        </div>
                    {/each}
                {/if}
            </div>
        </div>

        <div class="divider divider-horizontal mx-0" />

        <div class="flex flex-col flex-1 gap-y-4">
            <div class="flex w-full gap-x-4 items-center">
                <p class="font-bold">PreOp Questionnaire</p>

                <Select
                    bind:value={selectedPreOpQuestionnaire}
                    class="flex flex-1 max-h-[35px] rounded-full"
                    items={filteredPreOpQuestionnaires}
                    itemId="id"
                    label="name"
                    noOptionsMessage="No valid PreOp Questionnaires found"
                    placeholder={`${isLoadingPreOpQuestionnaire ? "Loading PreOp Questionnaires..." : "Search / Select PreOp Questionnaire"}`}
                    disabled={isLoadingPreOpQuestionnaire || isCreatingPreset}
                />
            </div>

            <p class="font-bold">Insurance types</p>

            {#if isSyncingInsuranceTypes}
                <PageLoading />
            {:else}
                <div class="flex flex-col flex-1 gap-y-4 overflow-y-auto overflow-x-visible">
                    {#each Object.entries(insuranceTypesByGroup).filter(([g]) => g !== '__ungrouped') as [groupName, items]}
                        <div class="border border-1 rounded-lg p-4 bg-white flex flex-col gap-4">
                            <div class="">{groupName}</div>
                            {#each items as insuranceType, i}
                                <div class="flex flex-col gap-y-4 border border-1 rounded-lg p-2 bg-gray-200/50">
                                    <div class="flex flex-row gap-2"> 
                                        <InfoIcon info={insuranceType.info} tooltipClass={"w-[400px]"} position=""></InfoIcon>
                                        <p>{insuranceType.description}</p>
                                    </div>

                                    <div class="flex flex-col gap-y-2 pl-4">
                                        <div
                                            class="flex items-center justify-between w-full"
                                        >
                                            <p class="text-sm">Billing type:</p>
                                            {#if showCreateBillingRuleIcon}
                                                <CustomTooltip tooltipPosition="left">
                                                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                                                    <!-- svelte-ignore a11y-no-static-element-interactions -->
                                                    <i
                                                        class="fa-solid fa-circle-plus cursor-pointer"
                                                        slot="tooltipTrigger"
                                                        on:click|preventDefault={() =>
                                                            handleClickCreateBillingRuleIcon(
                                                                i,
                                                            )}
                                                    >
                                                    </i>
                                                    <p>Create a new billing rule</p>
                                                </CustomTooltip>
                                            {/if}
                                        </div>
                                        <select
                                            class="input input-bordered input-sm"
                                            bind:value={insuranceType.billingRuleId}
                                        >
                                            <option value={0} selected>
                                                Please select
                                            </option>
                                            <option value="-1">
                                                Ask me every time
                                            </option>
                                            {#each doctorBillingRules.filter((dbr) => {
                                                if (presetData.useAsaCodes && dbr.type == 2) return dbr;
                                                else if (!presetData.useAsaCodes) return dbr;
                                            }) as billingRule}
                                                <option value={billingRule.id}>
                                                    {billingRule.name}
                                                </option>
                                            {/each}
                                        </select>
                                    </div>

                                    <div class="flex flex-col gap-y-2 pl-4">
                                        <p class="text-sm">Provider number to use:</p>
                                        <select
                                            class="input input-bordered input-sm"
                                            bind:value={insuranceType.doctorProviderId}
                                            on:change|preventDefault={(e) =>
                                                handleSelectProvider(
                                                    insuranceType.presetRuleType,
                                                    e.target.value,
                                                )}
                                        >
                                            <option value={0} selected>
                                                Automatic
                                            </option>
                                            {#each doctorProviders.filter((dp) => {
                                                if (insuranceType.presetRuleType != 6) return dp;

                                                return !dp.isPreferredDefence;
                                            }) as provider}
                                                <option value={provider.id}>
                                                    {provider.number}
                                                </option>
                                            {/each}
                                        </select>
                                    </div>

                                    {#if insuranceType.NibGapLabel}
                                        <p class="flex flex-1 pl-4 text-secondary">
                                            {insuranceType.NibGapLabel}
                                        </p>
                                    {/if}

                                    {#if insuranceType.showCapCheckbox}
                                        <div
                                            class="flex flex-1 gap-x-4 items-center pl-4"
                                        >
                                            <input
                                                type="checkbox"
                                                class="toggle toggle-secondary"
                                                bind:checked={insuranceType.maxGapProtection}
                                                id={i}
                                            />

                                            <label for={i} class="cursor-pointer">
                                                Cap the gap fee at the maximum allowance
                                                gap
                                            </label>
                                        </div>
                                    {/if}
                                </div>
                            {/each}
                        </div>
                    {/each}
                    {#each insuranceTypesByGroup.__ungrouped ?? [] as insuranceType, i}
                        <div
                            class="flex flex-col gap-y-4 border border-1 rounded-lg p-2 bg-gray-200/50"
                        >
                            <div class="flex flex-row gap-2">
                                <InfoIcon info={insuranceType.info} tooltipClass="w-[400px]" position="bottom"></InfoIcon>
                                <p>{insuranceType.description}</p>
                            </div>

                            <div class="flex flex-col gap-y-2 pl-4">
                                <div
                                    class="flex items-center justify-between w-full"
                                >
                                    <p class="text-sm">Billing type:</p>
                                    {#if showCreateBillingRuleIcon}
                                        <CustomTooltip tooltipPosition="bottom">
                                            <!-- svelte-ignore a11y-click-events-have-key-events -->
                                            <!-- svelte-ignore a11y-no-static-element-interactions -->
                                            <i
                                                class="fa-solid fa-circle-plus cursor-pointer"
                                                slot="tooltipTrigger"
                                                on:click|preventDefault={() =>
                                                    handleClickCreateBillingRuleIcon(
                                                        i,
                                                    )}
                                            >
                                            </i>
                                            <p>Create a new billing rule</p>
                                        </CustomTooltip>
                                    {/if}
                                </div>
                                <select
                                    class="input input-bordered input-sm"
                                    bind:value={insuranceType.billingRuleId}
                                >
                                    <option value={0} selected>
                                        Please select
                                    </option>
                                    <option value="-1">
                                        Ask me every time
                                    </option>
                                    {#each doctorBillingRules.filter((dbr) => {
                                        if (presetData.useAsaCodes && dbr.type == 2) return dbr;
                                        else if (!presetData.useAsaCodes) return dbr;
                                    }) as billingRule}
                                        <option value={billingRule.id}>
                                            {billingRule.name}
                                        </option>
                                    {/each}
                                </select>
                            </div>

                            <div class="flex flex-col gap-y-2 pl-4">
                                <p class="text-sm">Provider number to use:</p>
                                <select
                                    class="input input-bordered input-sm"
                                    bind:value={insuranceType.doctorProviderId}
                                    on:change|preventDefault={(e) =>
                                        handleSelectProvider(
                                            insuranceType.presetRuleType,
                                            e.target.value,
                                        )}
                                >
                                    <option value={0} selected>
                                        Automatic
                                    </option>
                                    {#each doctorProviders.filter((dp) => {
                                        if (insuranceType.presetRuleType != 6) return dp;

                                        return !dp.isPreferredDefence;
                                    }) as provider}
                                        <option value={provider.id}>
                                            {provider.number}
                                        </option>
                                    {/each}
                                </select>
                            </div>

                            {#if insuranceType.NibGapLabel}
                                <p class="flex flex-1 pl-4 text-secondary">
                                    {insuranceType.NibGapLabel}
                                </p>
                            {/if}

                            {#if insuranceType.showCapCheckbox}
                                <div
                                    class="flex flex-1 gap-x-4 items-center pl-4"
                                >
                                    <input
                                        type="checkbox"
                                        class="toggle toggle-secondary"
                                        bind:checked={insuranceType.maxGapProtection}
                                        id={i}
                                    />

                                    <label for={i} class="cursor-pointer">
                                        Cap the gap fee at the maximum allowance
                                        gap
                                    </label>
                                </div>
                            {/if}
                        </div>
                    {/each}
                </div>
            {/if}
        </div>
    </div>
</NotificationModal>

{#if isCreateNewBillingRuleModalOpen}
    <CreateBillingRuleModal
        closeModal={_closeCreateNewBillingRuleModal}
        onSubmit={handleAfterCreateBillingRule}
        selectedBillingRule={newBillingRuleObj}
        currentBillingRules={doctorBillingRules}
        {patientEncounter}
        {doctorDetails}
    />
{/if}

<style>
    .mandatory::after {
        content: "*";
        color: #f0a;
    }

    .title {
        width: 120px;
    }

    .w-remaining {
        width: calc(100% - 120px);
    }

    .preset-item {
        transition: font-weight 0.2s;
    }
    .preset-item.hovered {
        font-weight: bold;
    }
</style>
